import { useSyncExternalStore } from 'react';
import { ColorValue } from 'react-native';
import { DeviceTheme } from '../api/api.g';
import { Themes } from '../styles/theme';

export interface CustomTheme {
  isDark?: boolean;
  primary?: ColorValue;
  secondary?: ColorValue;
  secondaryLight?: ColorValue;
  mapControls?: ColorValue;
  background?: {
    secondary?: ColorValue;
    primary?: ColorValue;
    primaryLight?: ColorValue;
    modal?: ColorValue;
    navBar?: ColorValue;
    missionCard?: ColorValue;
    darkTurquoise?: ColorValue;
    missionDetail?: ColorValue;
    missionDetailSummary?: ColorValue;
    sea?: ColorValue;
    lightSea?: ColorValue;
  };
  text?: {
    primary?: ColorValue;
    inverse?: ColorValue;
    titleDark?: ColorValue;
    titleLight?: ColorValue;
    buttonCancel?: ColorValue;
    buttonConfirm?: ColorValue;
    darkTurquoiseTwo?: ColorValue;
    navItem?: ColorValue;
    navItemSelected?: ColorValue;
    sea?: ColorValue;
    error?: ColorValue;
  };
  menu?: {
    primary?: ColorValue;
    active?: ColorValue;
  };
  missionCard?: {
    flashBackgroundLight?: ColorValue;
    flashBackground?: ColorValue;
  };
  infoCell?: ColorValue;
  colorErrorPrimary?: ColorValue;
  colorErrorPrimaryBackground?: ColorValue;
  borderColor?: ColorValue;
  borderColorLight?: ColorValue;
  inputTextPlaceholder?: ColorValue;
  overlayColor?: ColorValue;
  indicatorColor?: ColorValue;
  timelineIcon?: ColorValue;
}

export const lightTheme: CustomTheme = {
  isDark: false,
  primary: '#f5a523',
  secondary: '#ECF6F5',
  secondaryLight: '#43a49e',
  mapControls: '#025164',
  background: {
    primary: 'white',
    primaryLight: 'white',
    secondary: '#f5f5f5',
    modal: '#e9f5f3',
    missionCard: '#e9f5f3',
    navBar: '#efefef',
    darkTurquoise: '#035164',
    missionDetail: '#ebf4f3',
    missionDetailSummary: '#d9eae7',
    sea: '#43a49e',
    lightSea: '#e9f5f3',
  },
  text: {
    primary: 'black',
    inverse: 'white',
    titleDark: '#025164',
    titleLight: '#43a49e',
    buttonCancel: 'black',
    buttonConfirm: '#43a49e',
    darkTurquoiseTwo: '#035164',
    navItem: '#1a1818',
    navItemSelected: '#43a49e',
    sea: '#43a49e',
    error: '#8e183d',
  },
  menu: {
    primary: 'white',
    active: '#f5a523',
  },
  missionCard: {
    flashBackgroundLight: '#fef6e8',
    flashBackground: '#fce6c1',
  },
  infoCell: 'white',
  colorErrorPrimary: '#8e183d',
  colorErrorPrimaryBackground: '#ffffff',
  borderColor: '#b6b6b6',
  borderColorLight: '#e9e9e9',
  inputTextPlaceholder: '#989898',
  overlayColor: '#03516484',
  indicatorColor: '#EE6723',
  timelineIcon: 'white',
};

const darkTheme: CustomTheme = {
  ...lightTheme,
  isDark: true,
  // Lighter dark greys to avoid near-black screens.
  primary: '#f0a020',
  secondaryLight: '#40a098',
  mapControls: '#30404c',
  secondary: '#344450',
  background: {
    ...lightTheme.background,
    primary: '#2b3a46',
    primaryLight: '#344450',
    secondary: '#344450',
    modal: '#465866',
    missionCard: '#465866',
    navBar: '#30404c',
    missionDetail: '#151a1f',
    missionDetailSummary: '#202830',
    sea: '#40a098',
    lightSea: '#3f4f5d',
  },
  text: {
    ...lightTheme.text,
    primary: 'white',
    inverse: 'black',
    // Keep body text white, but use differentiated accents to preserve hierarchy.
    // Use greener teal accents to stay aligned with Lanla brand direction.
    titleDark: '#74CCBE',
    titleLight: '#40a098',
    buttonCancel: '#f0a020',
    buttonConfirm: 'white',
    darkTurquoiseTwo: '#5FC8BA',
    navItem: 'white',
    navItemSelected: '#f0a020',
    sea: '#57C2B2',
    error: '#FF9BB4',
  },
  menu: {
    ...lightTheme.menu,
    primary: '#ffffff',
    active: '#f0a020',
  },
  missionCard: {
    flashBackgroundLight: '#4C606D',
    flashBackground: '#556A78',
  },
  infoCell: '#505050',
  borderColor: '#607383',
  borderColorLight: '#5F7486',
  overlayColor: '#00000080',
  indicatorColor: '#f0a020',
  timelineIcon: '#40a098',
  colorErrorPrimary: '#8e183d',
  colorErrorPrimaryBackground: '#ffffff',
};

const getThemeStore = () =>
  Themes.getInstance() ??
  Themes.createTheme({
    light: lightTheme,
    dark: darkTheme,
  });

const useCustomTheme = (): CustomTheme => {
  const themeStore = getThemeStore();
  const currentTheme = useSyncExternalStore(
    themeStore.subscribe.bind(themeStore),
    () => themeStore.currentTheme,
    () => themeStore.currentTheme,
  );

  return currentTheme === DeviceTheme.Dark ? themeStore.dark : themeStore.light;
};

export default useCustomTheme;
